import { useEffect, useState } from "react";

import { IconButton, TextField, Typography } from "@mui/material";
import { IoSearchOutline } from "react-icons/io5";

import { TCourse } from "../../../@types";
import { apis, callApi } from "../../../api";
import {
	useCourseCategories,
	useCourseCategoriesInit,
} from "../../../hooks/useCourseCategories";
import useScreenSize from "../../../hooks/useScreenSize";
import { useWhiteScreen } from "../../../hooks/useWhiteScreen";
import { eventNames, logEvent } from "../../../utils/logEvent";
import { Container } from "../../common/Container/Container";
import {
	CourseList,
	TSplittedCourses,
} from "../../common/CourseList/CourseList";
import noCourseImage from "../../imagenes/discuss.svg";

import "./orka_marketplace.scss";

export function OrkaMarketplace() {
	useWhiteScreen();
	const { width } = useScreenSize();
	const isMobile = width <= 768;

	const { categories } = useCourseCategories();
	useCourseCategoriesInit();

	type TState = {
		favoriteCourses: TCourse[];
		courses: TCourse[];
		courseName: string;
	};

	const [state, setState] = useState<TState>({
		favoriteCourses: [],
		courses: [],
		courseName: "",
	});

	useEffect(() => {
		getFavoriteCourses();
	}, []);

	const handleSearchCourse = async () => {
		try {
			type TBody = {
				fullsearch: string;
			};
			const body: TBody = {
				fullsearch: state.courseName.trim(),
			};

			const response = await callApi({
				method: "POST",
				url: apis.SEARCH_COURSES,
				body,
			});

			logEvent(eventNames.searchCourse, body);
			setState((prevState) => ({
				...prevState,
				courses: response ?? [],
				isSearch: true,
			}));
		} catch (error) {}
	};

	const getFavoriteCourses = async () => {
		try {
			const courses = await callApi({
				method: "GET",
				url: apis.GET_FAVORITE_COURSES,
			});

			setState((prevState) => ({
				...prevState,
				courses: courses ?? [],
				favoriteCourses: courses ?? [],
			}));
		} catch (error) {}
	};

	let splittedCourses: TSplittedCourses = categories.map((category) => ({
		category: category.name,
		courses: [],
	}));

	for (let i = 0; i < state.courses.length; i++) {
		const index = categories.findIndex(
			(category) => state.courses[i].category === category.name,
		);
		if (index === -1) {
			continue;
		}

		splittedCourses[index].courses.push(state.courses[i]);
	}

	splittedCourses = splittedCourses.filter(
		(section) => section.courses.length > 0,
	);

	const renderSearchBox = () => {
		return (
			<div className="caption-container">
				<Typography className="title bold">Connect with Instructors</Typography>
				<Typography className="description">
					Learn from the right person
				</Typography>

				<div className="search-box-text-input-container">
					<TextField
						value={state.courseName}
						onChange={(event) => {
							setState((prevState) => {
								if (event.target.value === "") {
									return {
										...prevState,
										courseName: event.target.value,
										courses: prevState.favoriteCourses,
									};
								}
								return {
									...prevState,
									courseName: event.target.value,
								};
							});
						}}
						fullWidth
						placeholder="Search by typing your goals like 'Get tailwheel endorsement'"
						className="search-box-text-input"
						slotProps={{
							input: { className: "normal-text" },
						}}
					/>
					<IconButton
						className="icon-button search-button"
						onClick={handleSearchCourse}>
						<IoSearchOutline />
					</IconButton>
				</div>
			</div>
		);
	};

	return (
		<Container
			className="orka-marketplace-container"
			navbarOrkaProps={{ white: true }}>
			{!isMobile && (
				<div className="for-instructor-image-container">
					<img
						src={require("../../imagenes/landing_page_background_desktop.png")}
						className="image"
					/>
					{renderSearchBox()}
				</div>
			)}

			<div className="orka-marketplace-content-container">
				{isMobile && (
					<>
						<Typography className="normal-title-text bold">
							Connect with Instructors
						</Typography>
						<Typography className="normal-text explore-description">
							Learn from the right person
						</Typography>
						<div className="search-box-text-input-container">
							<TextField
								value={state.courseName}
								onChange={(event) => {
									setState((prevState) => {
										if (event.target.value === "") {
											return {
												...prevState,
												courseName: event.target.value,
												courses: prevState.favoriteCourses,
											};
										}
										return {
											...prevState,
											courseName: event.target.value,
										};
									});
								}}
								fullWidth
								placeholder="What's your goal?"
								className="search-box-text-input"
								slotProps={{
									input: { className: "normal-text" },
								}}
							/>
							<IconButton
								className="icon-button search-button"
								onClick={handleSearchCourse}>
								<IoSearchOutline />
							</IconButton>
						</div>
					</>
				)}
				{state.courseName && state.courses.length === 0 && (
					<div className="no-courses-container">
						<Typography className="no-courses-text">
							Oh, there are no results with your keyword,
							<br />
							but don't worry. We are here to help you!
						</Typography>
						<img
							src={noCourseImage}
							className="no-result-image"
							alt="Something Great is coming"
							loading="lazy"
						/>
					</div>
				)}
				<CourseList splittedCourses={splittedCourses} className="course-list" />
			</div>
		</Container>
	);
}
